import { useEffect } from "react";

/**
 * Mounts dynamic Open Graph / Twitter meta tags into the document head.
 * Crawlers that execute JS (LinkedIn, WhatsApp 2024+, Twitter card validator,
 * Discord) will pick these up. For Facebook's older crawler, server-side
 * rendering would be required — use the OG image URL directly in those shares.
 *
 * Usage:
 *   <SocialMeta
 *     title="My brand logo"
 *     description="Generated by MakeYourBrand.IN AI"
 *     image="https://.../api/og/share/{id}"
 *     url={window.location.href}
 *   />
 */
export default function SocialMeta({ title, description, image, url }) {
  useEffect(() => {
    const tags = [
      { selector: 'meta[property="og:title"]', attr: "property", value: "og:title", content: title },
      { selector: 'meta[property="og:description"]', attr: "property", value: "og:description", content: description },
      { selector: 'meta[property="og:image"]', attr: "property", value: "og:image", content: image },
      { selector: 'meta[property="og:url"]', attr: "property", value: "og:url", content: url },
      { selector: 'meta[property="og:type"]', attr: "property", value: "og:type", content: "article" },
      { selector: 'meta[name="twitter:title"]', attr: "name", value: "twitter:title", content: title },
      { selector: 'meta[name="twitter:description"]', attr: "name", value: "twitter:description", content: description },
      { selector: 'meta[name="twitter:image"]', attr: "name", value: "twitter:image", content: image },
      { selector: 'meta[name="twitter:card"]', attr: "name", value: "twitter:card", content: "summary_large_image" },
    ];

    const previous = new Map();
    tags.forEach(({ selector, attr, value, content }) => {
      if (!content) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
        previous.set(el, { existed: false });
      } else {
        previous.set(el, { existed: true, content: el.getAttribute("content") });
      }
      el.setAttribute("content", content);
    });

    if (title) {
      const prevTitle = document.title;
      document.title = `${title} · MakeYourBrand.IN`;
      previous.set("title", prevTitle);
    }

    return () => {
      previous.forEach((info, el) => {
        if (el === "title") {
          document.title = info;
          return;
        }
        if (info.existed) {
          el.setAttribute("content", info.content || "");
        } else {
          el.remove();
        }
      });
    };
  }, [title, description, image, url]);

  return null;
}
